module.exports = new Promise((resolve) => {
    const remoteUrl = window.location.hostname === 'localhost' ? 'http://localhost:3013/remoteEntry.js' : window.location.origin + '/mfe/wfma/remoteEntry.js';
    const containerScope = 'websiteFileManager';

    // Keep a shared cache so duplicate remote aliases don't trigger extra script loads.
    const globalWindow = window;
    globalWindow.__platformShellRemoteLoaders = globalWindow.__platformShellRemoteLoaders || {};
    globalWindow.__platformShellRemoteInits = globalWindow.__platformShellRemoteInits || {};

    // Creates a loader promise for a specific remote module if it doesn't exist or pulls it from the global loader cache if it does, and returns it
    const loadContainer = () => {
      if (globalWindow[containerScope]) return Promise.resolve(globalWindow[containerScope]);

      const existingLoader = globalWindow.__platformShellRemoteLoaders[containerScope];
      if (existingLoader) return existingLoader;

      const loader = new Promise((resolveContainer, rejectContainer) => {
        const script = document.createElement('script');
        script.src = remoteUrl;
        script.type = 'text/javascript';
        script.async = true;
        script.setAttribute('data-webpack', containerScope);

        script.onload = () => {
          const container = globalWindow[containerScope];
          if (!container) {
            rejectContainer(new Error('Remote container ' + containerScope + ' is unavailable after script load'));
            return;
          }
          resolveContainer(container);
        };

        script.onerror = () => {
          script.remove();
          delete globalWindow.__platformShellRemoteLoaders[containerScope];
          rejectContainer(new Error('Remote ' + containerScope + ' failed to load'));
        };

        document.head.appendChild(script);
      });

      globalWindow.__platformShellRemoteLoaders[containerScope] = loader;
      return loader;
    };

    // Creates a container init promise for a specific remote module if it doesn't exist or pulls it from the global init cache if it does, and returns it
    const initializeContainer = (container, sharedScope) => {
      const initCache = globalWindow.__platformShellRemoteInits;
      if (initCache[containerScope]) return initCache[containerScope];

      const initPromise = Promise.resolve()
        .then(() => container.init(sharedScope))
        .catch((err) => {
          const msg = err?.message ?? '';
          if (!msg.includes('already been initialized')) {
            delete initCache[containerScope];
            throw err;
          }
        });

      initCache[containerScope] = initPromise;
      return initPromise;
    };

    // Stores the shared scope (webpack shared module config) between init() and get() calls.
    let pendingSharedScope;

    // The proxy mimics a webpack container interface (init + get) so webpack treats it like a real remote.
    // - init(sharedScope) is called first by webpack to pass shared dependencies (e.g. React, ReactDOM).
    // - get(request) is called later to retrieve a specific module from the remote container.
    const proxy = {
      get: (request) =>
        loadContainer().then((container) => {
          const runGet = () => container.get(request);
          // If init() hasn't been called yet, skip initialization and just get the module.
          if (!pendingSharedScope) return runGet();
          // Otherwise, ensure the container is initialized with shared deps before getting the module.
          return initializeContainer(container, pendingSharedScope).then(runGet);
        }),
      init: (sharedScope) => {
        // Capture the shared scope for deferred use in get() when the container hasn't loaded yet.
        pendingSharedScope = sharedScope;
        // If the container is already available (e.g. script was cached), initialize it immediately.
        if (!globalWindow[containerScope]) return Promise.resolve();
        return initializeContainer(globalWindow[containerScope], sharedScope);
      }
    };

    resolve(proxy);
  });